/**
 * Normalize a value for matching (lowercase, trim).
 */
function n(s) {
    return (s ?? '').toString().trim().toLowerCase();
}
/**
 * A template "looks cardiac" if any of its body-area / name / modality
 * fields mention cardiac terms. We boost these when the study has been
 * classified as an echo — a generic US template wouldn't win over a
 * dedicated cardiac one.
 */
const CARDIAC_REGEX = /echo|cardiac|heart|cardio/i;
function isCardiacTemplate(t) {
    if (CARDIAC_REGEX.test(t.name))
        return true;
    return (t.bodyAreas ?? []).some((b) => CARDIAC_REGEX.test(b));
}
/**
 * Loose two-way substring match (handles "thorax" vs "thorax - 3 view").
 * Only safe for body areas, where descriptions are verbose and free-text. It
 * over-matches on short coded values (e.g. "ovine" ⊂ "bovine", "ct" ⊂ "oct"),
 * so species/modality use exact match instead — see {@link matchesExact}.
 */
function matchesLoosely(haystack, needle) {
    if (!haystack || !needle)
        return false;
    return haystack.includes(needle) || needle.includes(haystack);
}
/** Exact (normalized) match, or the template value is a wildcard sentinel. */
function matchesExact(value, templateValue) {
    if (!value || !templateValue)
        return false;
    return templateValue === value || templateValue === 'all' || templateValue === '*';
}
/**
 * Penalty for a template that declares one or more modalities, none of which
 * match the study's modality. Larger than every bonus combined (species 10 +
 * body area 8 + echo 12 = 30), so a modality-mismatched template can never
 * outrank one whose modality matches (worth at least +5), and its score goes
 * negative, so it is never the "Recommended" pick either
 * ({@link hasStrictTopRecommendation} requires a positive top score).
 * Templates that declare no modalities stay neutral, as does a study with no
 * modality to judge against.
 */
export const MODALITY_MISMATCH_PENALTY = 100;
/** A template is a candidate when its type matches, or it has no type (personal). */
function passesTypeFilter(t, wanted) {
    if (!wanted)
        return true;
    return t.templateType === wanted || t.templateType == null;
}
/**
 * Single scoring function shared by the template picker (standard) and the
 * teleconsultation auto-select. Returns every candidate template with its score,
 * highest first.
 */
export function scoreTemplates(templates, input) {
    const species = n(input.species);
    const modality = n(input.modality);
    const bodyArea = n(input.bodyArea);
    return (templates
        .filter((t) => passesTypeFilter(t, input.templateType))
        .map((t) => {
        let score = 0;
        // Species: exact match > "all"/"*". Exact (not substring) so "ovine"
        // doesn't match a "bovine" template.
        const templateSpecies = (t.species ?? []).map((s) => n(s));
        if (species && templateSpecies.some((s) => s === species))
            score += 10;
        else if (templateSpecies.includes('all') || templateSpecies.includes('*'))
            score += 3;
        // Modality: exact match or wildcard sentinel — substring would let "ct"
        // match an "oct" template. A template that names modalities and misses
        // on all of them is penalised (see MODALITY_MISMATCH_PENALTY): without
        // this, "Canine Thorax (DX, CR)" outscored "Standard CT" on a canine CT
        // thorax because species + body area were worth more than modality.
        const templateModalities = (t.modalities ?? []).map((m) => n(m));
        if (modality && templateModalities.length > 0) {
            if (templateModalities.some((m) => matchesExact(modality, m)))
                score += 5;
            else
                score -= MODALITY_MISMATCH_PENALTY;
        }
        // Body area (empty array on template = any body area for that modality)
        const templateBodyAreas = (t.bodyAreas ?? []).map((b) => n(b));
        if (bodyArea && templateBodyAreas.length > 0) {
            if (templateBodyAreas.some((b) => matchesLoosely(bodyArea, b)))
                score += 8;
        }
        else if (templateBodyAreas.length === 0) {
            score += 2; // no body area filter = matches any
        }
        // Echo boost — let cardiac templates outrank generic US on echo studies.
        if (input.isEcho && isCardiacTemplate(t))
            score += 12;
        return { template: t, score };
    })
        // Score desc, then name asc so ties have a stable, human-friendly order.
        .sort((a, b) => b.score - a.score || a.template.name.localeCompare(b.template.name)));
}
/**
 * Whether the top-scored template is a clear, unambiguous recommendation:
 * its score is positive AND strictly higher than the runner-up. On a tie we
 * recommend nothing rather than arbitrarily picking the alphabetical winner.
 */
export function hasStrictTopRecommendation(scored) {
    if (scored.length === 0 || scored[0].score <= 0)
        return false;
    return scored.length === 1 || scored[0].score > scored[1].score;
}
/**
 * Best-matching template for the given context, or null when nothing scores or
 * the top score ties with the runner-up (an ambiguous match recommends none).
 */
export function findBestTemplate(templates, input) {
    const scored = scoreTemplates(templates, input);
    return hasStrictTopRecommendation(scored) ? scored[0].template : null;
}
/**
 * Find the best-matching teleconsultation template for a consultation case.
 * Thin wrapper over {@link findBestTemplate} that keeps the teleconsultation
 * fallback (generic "all species, no modality" template).
 */
/**
 * Full teleconsultation template resolution for the TC editor's auto-select:
 * the server-resolved default (connection default_template_id, else the
 * receiving group's defaults-matrix match) wins when it maps to a loaded
 * teleconsultation template; otherwise fall back to the client-side scoring
 * heuristic. Manual template changes are unaffected — this only seeds the
 * initial pick.
 */
export function resolveTeleconsultTemplate(templates, caseData, ctx = {}) {
    const resolvedId = caseData.resolvedDefaultTemplateId;
    if (resolvedId) {
        const configured = templates.find((t) => t.id === resolvedId && t.templateType === 'teleconsultation');
        if (configured)
            return configured;
    }
    return findBestTeleconsultTemplate(templates, caseData, ctx);
}
export function findBestTeleconsultTemplate(templates, caseData, ctx = {}) {
    const teleTemplates = templates.filter((t) => t.templateType === 'teleconsultation');
    if (teleTemplates.length === 0)
        return null;
    const best = findBestTemplate(teleTemplates, {
        species: caseData.patient.species,
        modality: caseData.study.modality,
        bodyArea: caseData.study.bodyArea,
        templateType: 'teleconsultation',
        isEcho: ctx.isEcho,
    });
    if (best)
        return best;
    // Fallback: generic teleconsultation (All species, no modality filter)
    const generic = teleTemplates.find((t) => {
        const speciesList = (t.species ?? []).map((s) => n(s));
        return speciesList.includes('all') && (t.modalities ?? []).length === 0;
    });
    return generic ?? null;
}
